/**
 * Sound Service
 *
 * Handles notification sound playback using Web Audio API
 * Generates tones programmatically - no external sound files required
 *
 * Features:
 * - Different tones for different notification types
 * - Volume control per notification type
 * - Fallback to silent if Web Audio API not supported
 */

import apiClient from './api.client';

// Synthesized sounds (Web Audio API)
export type SynthSoundType = 'default' | 'bell' | 'chime' | 'pop' | 'mention' | 'voiceJoin' | 'voiceLeave' | 'callRingtone';
// Audio file sounds
export type FileSoundType = 'pleasant' | 'message' | 'received' | 'received2' | 'alert' | 'pop2';
// Combined type (includes custom sounds as custom:<uuid>)
export type SoundType = SynthSoundType | FileSoundType | 'none' | `custom:${string}`;
export type NotificationType = 'message' | 'mention' | 'dm' | 'system';

interface SoundConfig {
  frequency: number; // Hz
  duration: number; // ms
  type: OscillatorType;
  pattern?: Array<{ freq: number; duration: number }>; // For multi-tone sounds
}

// Audio file paths for file-based sounds
const AUDIO_FILES: Record<FileSoundType, string> = {
  pleasant: '/sounds/pleasant.mp3',
  message: '/sounds/message.mp3',
  received: '/sounds/received.mp3',
  received2: '/sounds/received2.mp3',
  alert: '/sounds/alert.mp3',
  pop2: '/sounds/pop2.wav',
};

// Sound configurations for synthesized sounds
const SOUND_CONFIGS: Record<SynthSoundType, SoundConfig> = {
  default: {
    frequency: 440, // A4 note
    duration: 200,
    type: 'sine',
  },
  bell: {
    frequency: 659, // E5 note
    duration: 150,
    type: 'sine',
    pattern: [
      { freq: 659, duration: 100 },
      { freq: 523, duration: 100 },
    ],
  },
  chime: {
    frequency: 523, // C5 note
    duration: 200,
    type: 'triangle',
    pattern: [
      { freq: 523, duration: 80 },
      { freq: 659, duration: 80 },
      { freq: 784, duration: 120 },
    ],
  },
  pop: {
    frequency: 880, // A5 note
    duration: 80,
    type: 'square',
  },
  mention: {
    frequency: 784, // G5 note (attention-grabbing, higher than regular messages)
    duration: 300,
    type: 'sine',
    pattern: [
      { freq: 659, duration: 80 },  // E5
      { freq: 784, duration: 100 }, // G5
      { freq: 988, duration: 120 }, // B5 (ascending pattern = important notification)
    ],
  },
  voiceJoin: {
    frequency: 659, // E5 note (welcoming, high)
    duration: 120,
    type: 'sine',
    pattern: [
      { freq: 523, duration: 60 },  // C5
      { freq: 659, duration: 80 },  // E5 (ascending pattern = "joining")
    ],
  },
  voiceLeave: {
    frequency: 392, // G4 note (lower, goodbye)
    duration: 150,
    type: 'sine',
    pattern: [
      { freq: 523, duration: 60 },  // C5
      { freq: 392, duration: 100 }, // G4 (descending pattern = "leaving")
    ],
  },
  callRingtone: {
    frequency: 440, // A4
    duration: 600,
    type: 'sine',
    pattern: [
      { freq: 523, duration: 150 }, // C5
      { freq: 659, duration: 150 }, // E5
      { freq: 523, duration: 150 }, // C5
      { freq: 659, duration: 200 }, // E5 (two-tone ring pattern)
    ],
  },
};

// Helper to check if a sound type is file-based
function isFileSoundType(soundType: SoundType): soundType is FileSoundType {
  return soundType in AUDIO_FILES;
}

// Helper to check if a sound type is a custom uploaded sound
function isCustomSound(soundType: SoundType): boolean {
  return typeof soundType === 'string' && soundType.startsWith('custom:');
}

// Extract sound ID from custom:<uuid> format
function getCustomSoundId(soundType: string): string {
  return soundType.replace('custom:', '');
}

class SoundService {
  private audioContext: AudioContext | null = null;
  private enabled = true;
  private audioCache: Map<string, HTMLAudioElement> = new Map();

  constructor() {
    // Initialize Web Audio API
    if (typeof window !== 'undefined' && (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)) {
      try {
        this.audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
      } catch (error) {
        console.warn('Web Audio API not available:', error);
        this.audioContext = null;
      }
    }
  }

  /**
   * Play a notification sound (either audio file or synthesized)
   */
  async playNotification(
    soundType: SoundType,
    volume: number = 50
  ): Promise<void> {
    if (!this.enabled || soundType === 'none' || volume === 0) {
      return;
    }

    const normalizedVolume = Math.max(0, Math.min(100, volume)) / 100;

    try {
      // Check if it's a custom uploaded sound
      if (isCustomSound(soundType)) {
        await this.playCustomSound(getCustomSoundId(soundType), normalizedVolume);
        return;
      }

      // Check if it's a file-based sound
      if (isFileSoundType(soundType)) {
        await this.playAudioFile(AUDIO_FILES[soundType], normalizedVolume);
        return;
      }

      // Otherwise, play synthesized sound
      if (!this.audioContext) {
        return;
      }

      // Resume audio context if suspended (required for autoplay policies)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const config = SOUND_CONFIGS[soundType as SynthSoundType];
      if (config.pattern) {
        // Play multi-tone pattern
        await this.playPattern(config.pattern, normalizedVolume);
      } else {
        // Play single tone
        await this.playTone(
          config.frequency,
          config.duration,
          config.type,
          normalizedVolume
        );
      }
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  }

  /**
   * Play an audio file
   */
  private async playAudioFile(path: string, volume: number): Promise<void> {
    return new Promise((resolve, reject) => {
      // Use cached audio element or create new one
      let audio = this.audioCache.get(path);
      if (!audio) {
        audio = new Audio(path);
        this.audioCache.set(path, audio);
      }

      audio.volume = volume;
      audio.currentTime = 0;

      const onEnded = () => {
        audio!.removeEventListener('ended', onEnded);
        audio!.removeEventListener('error', onError);
        resolve();
      };

      const onError = (e: Event) => {
        audio!.removeEventListener('ended', onEnded);
        audio!.removeEventListener('error', onError);
        console.error('Audio playback error:', e);
        reject(e);
      };

      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onError);

      audio.play().catch((err) => {
        console.error('Failed to play audio:', err);
        reject(err);
      });
    });
  }

  /**
   * Play a single tone
   */
  private async playTone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number
  ): Promise<void> {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    // Envelope: quick fade in, sustain, quick fade out
    const now = this.audioContext.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume * 0.3, now + 0.01); // Fade in
    gainNode.gain.exponentialRampToValueAtTime(volume * 0.1, now + duration / 1000 - 0.01); // Sustain
    gainNode.gain.linearRampToValueAtTime(0.001, now + duration / 1000); // Fade out

    oscillator.start(now);
    oscillator.stop(now + duration / 1000);

    return new Promise((resolve) => {
      setTimeout(resolve, duration);
    });
  }

  /**
   * Play a pattern of tones
   */
  private async playPattern(
    pattern: Array<{ freq: number; duration: number }>,
    volume: number
  ): Promise<void> {
    for (const note of pattern) {
      await this.playTone(note.freq, note.duration, 'sine', volume);
      // Small gap between notes
      await new Promise((resolve) => setTimeout(resolve, 20));
    }
  }

  /**
   * Play a custom uploaded sound by fetching from API and caching as Blob URL
   */
  private async playCustomSound(soundId: string, volume: number): Promise<void> {
    const cacheKey = `custom:${soundId}`;
    let audio = this.audioCache.get(cacheKey);

    if (!audio) {
      // Fetch audio blob via API client (handles auth)
      const response = await apiClient.get(`/custom-sounds/${soundId}/audio`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      audio = new Audio(blobUrl);
      this.audioCache.set(cacheKey, audio);
    }

    await this.playAudioElement(audio, volume);
  }

  /**
   * Play an HTMLAudioElement with given volume
   */
  private async playAudioElement(audio: HTMLAudioElement, volume: number): Promise<void> {
    return new Promise((resolve, reject) => {
      audio.volume = volume;
      audio.currentTime = 0;

      const onEnded = () => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
        resolve();
      };

      const onError = (e: Event) => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
        console.error('Audio playback error:', e);
        reject(e);
      };

      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onError);

      audio.play().catch((err) => {
        console.error('Failed to play audio:', err);
        reject(err);
      });
    });
  }

  /**
   * Clear cached custom sound (call when a custom sound is deleted)
   */
  clearCustomSoundCache(soundId: string): void {
    const cacheKey = `custom:${soundId}`;
    const audio = this.audioCache.get(cacheKey);
    if (audio) {
      URL.revokeObjectURL(audio.src);
      this.audioCache.delete(cacheKey);
    }
  }

  /**
   * Play a ringtone in a loop (for incoming calls)
   * Returns a stop function to cancel the loop
   */
  playRingtone(soundType: SoundType = 'callRingtone', volume: number = 70): { stop: () => void } {
    let stopped = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const playOnce = () => {
      if (stopped) return;
      this.playNotification(soundType, volume).catch(() => {});
    };

    // Play immediately, then repeat every 2.5 seconds
    playOnce();
    intervalId = setInterval(playOnce, 2500);

    return {
      stop: () => {
        stopped = true;
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      },
    };
  }

  /**
   * Enable/disable all sounds
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Test a sound (useful for settings preview)
   */
  async testSound(soundType: SoundType, volume: number = 50): Promise<void> {
    await this.playNotification(soundType, volume);
  }

  /**
   * Check if Web Audio API is available
   */
  isAvailable(): boolean {
    return this.audioContext !== null;
  }
}

// Export singleton instance
export const soundService = new SoundService();

// Convenience functions for playing specific notification types
export async function playMessageSound(soundType: SoundType, volume: number) {
  await soundService.playNotification(soundType, volume);
}

export async function playMentionSound(soundType: SoundType, volume: number) {
  await soundService.playNotification(soundType, volume);
}

export async function playDMSound(soundType: SoundType, volume: number) {
  await soundService.playNotification(soundType, volume);
}

export async function playSystemSound(soundType: SoundType, volume: number) {
  await soundService.playNotification(soundType, volume);
}

export async function playVoiceJoinSound(volume: number = 50) {
  await soundService.playNotification('voiceJoin', volume);
}

export async function playVoiceLeaveSound(volume: number = 50) {
  await soundService.playNotification('voiceLeave', volume);
}
